import { useRef, useState } from 'react';
import type { ChangeEvent, ClipboardEvent, FormEvent, KeyboardEvent } from 'react';

// Keys that mean the user is actually entering/editing text (not navigating).
// Single-character keys (letters/digits/symbols/space) report a 1-char `key`,
// while Tab/Enter/Arrow*/Shift… report multi-char names and are ignored — so
// merely tabbing through an autofilled field never counts as typing.
const isTypingKey = (key: string) =>
  key.length === 1 || key === 'Backspace' || key === 'Delete';

export interface PasswordRevealField {
  /** Whether the field value is currently shown in clear text. */
  visible: boolean;
  /** Whether the reveal toggle should be rendered at all. */
  canReveal: boolean;
  /** Flip the masked/clear state. */
  toggle: () => void;
  /** Props to spread onto the password <input> (it owns value via setValue). */
  inputProps: {
    type: 'text' | 'password';
    onChange: (e: ChangeEvent<HTMLInputElement>) => void;
    onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void;
    onBeforeInput: (e: FormEvent<HTMLInputElement>) => void;
    onPaste: (e: ClipboardEvent<HTMLInputElement>) => void;
  };
}

/**
 * Drives a "show / hide password" toggle that can never unmask credentials the
 * user did not type themselves.
 *
 * Threat model: a stored password (filled by the browser or a password manager)
 * must never be revealable by a bystander. Autofill can happen at any time —
 * including AFTER the user has typed and revealed their own password, or
 * replacing the current value via the browser's account dropdown.
 *
 * Invariants enforced:
 *  1. Only a value the user typed/pasted (`typed`) is ever revealable. A value
 *     placed by autofill fires a change event with NO preceding user-input event
 *     (keydown / beforeinput / paste), so it is detected and forced back to
 *     masked + non-revealable every time it arrives — even if the field was
 *     revealed a moment earlier.
 *  2. The first time the user edits a still-untyped (autofilled) value, the
 *     stored secret is WIPED before the edit commits, so it can never be
 *     appended to and then revealed. The DOM value is read/cleared directly to
 *     also cover browsers that autofill without updating React state.
 *
 * The hook does not own the value (the page needs it for submit/validation); it
 * is given `value` + `setValue` and drives the field's onChange itself.
 */
export function usePasswordReveal(
  value: string,
  setValue: (v: string) => void,
): PasswordRevealField {
  const [show, setShow] = useState(false);
  const [typed, setTyped] = useState(false);
  // Set by a genuine user-input event, consumed by the change it produces. Lets
  // onChange tell real edits apart from autofill (which emits no such event).
  const userInput = useRef(false);

  const canReveal = typed && value.length > 0;
  const visible = show && canReveal;

  // Wipe an autofilled (never-typed) secret before the user's first edit lands.
  // Reads the live DOM value so it works even when autofill never reached React
  // state. Returns true when it wiped (caller should cancel the edit).
  const wipeAutofillBeforeEdit = (el: HTMLInputElement): boolean => {
    if (!typed && el.value.length > 0) {
      el.value = '';
      setValue('');
      userInput.current = false;
      return true;
    }
    return false;
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!isTypingKey(e.key)) return;
    userInput.current = true;
    if (wipeAutofillBeforeEdit(e.currentTarget)) e.preventDefault();
  };

  // beforeinput fires for genuine user input on every platform (including mobile
  // soft keyboards, where keydown is unreliable) and never for autofill.
  const onBeforeInput = (e: FormEvent<HTMLInputElement>) => {
    userInput.current = true;
    if (wipeAutofillBeforeEdit(e.currentTarget)) e.preventDefault();
  };

  const onPaste = (e: ClipboardEvent<HTMLInputElement>) => {
    userInput.current = true;
    if (!typed && e.currentTarget.value.length > 0) {
      // Pasting into an autofilled field replaces the stored secret with only
      // what was pasted, so the autofilled value is never retained or revealed.
      e.preventDefault();
      const pasted = e.clipboardData.getData('text');
      e.currentTarget.value = pasted;
      setValue(pasted);
      setTyped(pasted.length > 0);
      userInput.current = false;
    }
  };

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    setValue(next);
    if (userInput.current) {
      userInput.current = false;
      setTyped(next.length > 0);
    } else {
      // Change with no preceding user-input event → autofill / programmatic fill
      // (including selecting another saved account). Seal it: never typed, and
      // force the toggle back to masked so a stored secret is never shown.
      setTyped(false);
      setShow(false);
    }
  };

  return {
    visible,
    canReveal,
    toggle: () => setShow((v) => !v),
    inputProps: { type: visible ? 'text' : 'password', onChange, onKeyDown, onBeforeInput, onPaste },
  };
}
